'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Copy01IconSolid } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';
import { emitShareEvent, normalizeShareSurface, type ShareSurfaceInput } from './events';
import type { ShareChannel } from './intents';
import { canNativeShare, copyToClipboard, nativeShare, openShareIntent } from './share';
import { ShareDirectTrigger } from './share-direct-trigger';
import { ShareOgPreview } from './share-og-preview';
import { SharePlatformRow } from './share-platform-row';
import { PLATFORM_CHANNELS, type PlatformChannel } from './share-platforms';
import type { CopyShareLinkToast } from './use-copy-share-link';
export type CopiedKind = 'link' | 'message';
export interface ShareModalProps {
  /** Element that opens the modal — typically a button. Must be a single
   *  React element so the modal can wire its own click handler when in
   *  `directChannel` mode. Optional when the modal is driven imperatively
   *  via `useShareModalStore` + `<GlobalShareModal />`. */
  trigger?: ReactNode;
  /** Controlled `open` state. When provided alongside `onOpenChange`, the
   *  modal hands its open/close lifecycle to the caller — used by the
   *  imperative `<GlobalShareModal />` driven by the share-modal store.
   *  Mutually exclusive with `directChannel`. */
  open?: boolean;
  /** Controlled `onOpenChange` handler — see `open` above. */
  onOpenChange?: (open: boolean) => void;
  /** Canonical URL being shared. Pass an empty string only when the share
   *  text already embeds the URL. */
  url: string;
  /** Live data revision that invalidates the generated preview. */
  previewVersion?: string | undefined;
  /** Pre-composed share text used for X/Telegram/WhatsApp/native. */
  text?: string;
  /** Per-channel text overrides. When a channel handler runs, it picks
   *  `textPerChannel[channel]` if present, otherwise falls back to `text`.
   *  Useful for channels with character limits or different framing. */
  textPerChannel?: Partial<Record<ShareChannel, string>>;
  /** Title shown in the dialog header AND used for native share. Defaults
   *  to a generic localized "Share". */
  title?: ReactNode;
  /** Optional description rendered below the title. */
  description?: ReactNode;
  /** Identifies the surface for analytics — REQUIRED. Accepts the
   *  shorthand string (e.g. `'support_mainnet'`) or the full object
   *  (e.g. `{ kind: 'market', marketPubkey }`). */
  surface: ShareSurfaceInput;
  /** Toast shown on successful link copy from inside the modal. Defaults
   *  to a localized "Link copied" toast. Pass `null` to disable. */
  copyToast?: CopyShareLinkToast | null;
  /** Platform channels to render in the row. Defaults to all registered
   *  platforms (X, Telegram, WhatsApp, Reddit). */
  channels?: readonly PlatformChannel[];
  /** Whether to render the "or copy link" field. Default `true`. */
  showCopy?: boolean;
  /** Whether to render the native-share button when supported. Default `true`. */
  showNative?: boolean;
  /** Fires after a successful platform / native share. Receives the channel
   *  used (`'native'` for the OS sheet). */
  onShared?: (channel: ShareChannel | 'native') => void;
  /** Fires after a successful copy. Always `'link'` from inside the
   *  modal; `'message'` is reserved for legacy callers that still wire
   *  the kind into their own copy surfaces (e.g. proposal-created
   *  success). */
  onCopied?: (kind: CopiedKind) => void;
  /** When set, clicking the trigger fires this channel directly without
   *  rendering the modal. Use for surfaces with intrinsic single-channel
   *  UX such as the gift "Thank on X" CTA. */
  directChannel?: ShareChannel;
}
export function ShareModal(props) {
  const $ = _c(69);
  const t = useTranslations("share.modal");
  let t0;
  if ($[0] !== props.surface) {
    t0 = normalizeShareSurface(props.surface);
    $[0] = props.surface;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const surface = t0;
  const isControlled = props.open !== undefined && props.onOpenChange !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? props.open ?? false : internalOpen;
  let t1;
  if ($[2] !== isControlled || $[3] !== props) {
    t1 = isControlled ? next => {
      props.onOpenChange?.(next);
    } : setInternalOpen;
    $[2] = isControlled;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setOpen = t1;
  const [hasNative, setHasNative] = useState(false);
  const [copied, setCopied] = useState(false);
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHasNative(canNativeShare());
    };
    t3 = [];
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== copied) {
    t4 = () => {
      if (!copied) {
        return;
      }
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => {
        clearTimeout(timer);
      };
    };
    t5 = [copied];
    $[7] = copied;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  if (props.directChannel !== undefined) {
    const t6 = props.trigger ?? null;
    let t7;
    if ($[10] !== props.text) {
      t7 = props.text !== undefined ? {
        text: props.text
      } : {};
      $[10] = props.text;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== props.onShared) {
      t8 = props.onShared !== undefined ? {
        onShared: props.onShared
      } : {};
      $[12] = props.onShared;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== props.directChannel || $[15] !== props.url || $[16] !== surface || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
      t9 = <ShareDirectTrigger trigger={t6} channel={props.directChannel} url={props.url} {...t7} surface={surface} {...t8} />;
      $[14] = props.directChannel;
      $[15] = props.url;
      $[16] = surface;
      $[17] = t6;
      $[18] = t7;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  const channels = props.channels ?? PLATFORM_CHANNELS;
  const showCopy = props.showCopy ?? true;
  const showNative = (props.showNative ?? true) && hasNative;
  let t6;
  if ($[21] !== props.text || $[22] !== props.textPerChannel) {
    t6 = function textForChannel(channel) {
      const override = props.textPerChannel?.[channel];
      return override ?? props.text;
    };
    $[21] = props.text;
    $[22] = props.textPerChannel;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const textForChannel = t6;
  let t7;
  if ($[24] !== props || $[25] !== setOpen || $[26] !== surface || $[27] !== t) {
    t7 = async function handleCopyLink() {
      const ok = await copyToClipboard(props.url);
      if (!ok) {
        return;
      }
      setCopied(true);
      emitShareEvent(surface, "copy");
      const toast = props.copyToast === undefined ? {
        id: TOAST_IDS.SHARE_LINK_COPIED,
        message: t("linkCopied")
      } : props.copyToast;
      if (toast !== null) {
        showToast({
          id: toast.id,
          message: toast.message,
          variant: "success",
          ...(toast.duration !== undefined ? {
            duration: toast.duration
          } : {})
        });
      }
      props.onCopied?.("link");
      setOpen(false);
    };
    $[24] = props;
    $[25] = setOpen;
    $[26] = surface;
    $[27] = t;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const handleCopyLink = t7;
  let t8;
  if ($[29] !== props || $[30] !== setOpen || $[31] !== surface || $[32] !== textForChannel) {
    t8 = function handleChannel(channel_0) {
      const channelText = textForChannel(channel_0);
      openShareIntent(channel_0, {
        url: props.url,
        ...(channelText !== undefined ? {
          text: channelText
        } : {}),
        surface
      });
      props.onShared?.(channel_0);
      setOpen(false);
    };
    $[29] = props;
    $[30] = setOpen;
    $[31] = surface;
    $[32] = textForChannel;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const handleChannel = t8;
  let t9;
  if ($[34] !== props || $[35] !== setOpen || $[36] !== surface || $[37] !== textForChannel) {
    t9 = async function handleNative() {
      const channelText_0 = textForChannel("x");
      const result = await nativeShare({
        url: props.url,
        ...(channelText_0 !== undefined ? {
          text: channelText_0
        } : {}),
        ...(typeof props.title === "string" ? {
          title: props.title
        } : {}),
        surface
      });
      if (result === "shared") {
        props.onShared?.("native");
        setOpen(false);
      }
    };
    $[34] = props;
    $[35] = setOpen;
    $[36] = surface;
    $[37] = textForChannel;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const handleNative = t9;
  let t10;
  if ($[39] !== props.title || $[40] !== t) {
    t10 = props.title !== undefined && props.title !== null && props.title !== "" ? props.title : t("title");
    $[39] = props.title;
    $[40] = t;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const dialogTitle = t10;
  const showPlatformRow = channels.length > 0 || showNative;
  let t11;
  if ($[42] !== open || $[43] !== props.previewVersion || $[44] !== surface) {
    t11 = open && <ShareOgPreview surface={surface} version={props.previewVersion} />;
    $[42] = open;
    $[43] = props.previewVersion;
    $[44] = surface;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== channels || $[47] !== handleChannel || $[48] !== handleNative || $[49] !== showNative || $[50] !== showPlatformRow) {
    t12 = showPlatformRow && <SharePlatformRow channels={channels} showNative={showNative} onChannel={handleChannel} onNative={() => {
      handleNative();
    }} />;
    $[46] = channels;
    $[47] = handleChannel;
    $[48] = handleNative;
    $[49] = showNative;
    $[50] = showPlatformRow;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== copied || $[53] !== handleCopyLink || $[54] !== props.url || $[55] !== showCopy || $[56] !== t) {
    t13 = showCopy && props.url.length > 0 && <div className="flex min-w-0 flex-col gap-2"><p className="text-caption font-medium text-foreground-secondary">{t("orCopyLink")}</p><div className="flex min-w-0 items-center gap-2"><InputField value={props.url} readOnly={true} disabled={true} fullWidth={true} containerClassName="flex-1 min-w-0" className="bg-background-tertiary text-body md:text-caption disabled:bg-background-tertiary dark:bg-background-tertiary" /><AnimatedButton type="button" variant="secondary" onClick={() => {
          handleCopyLink();
        }} aria-label={copied ? t("copied") : t("copy")} className="size-10 shrink-0 rounded-control p-0"><Icon icon={Copy01IconSolid} className="size-4" aria-hidden="true" /></AnimatedButton></div></div>;
    $[52] = copied;
    $[53] = handleCopyLink;
    $[54] = props.url;
    $[55] = showCopy;
    $[56] = t;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t11 || $[59] !== t12 || $[60] !== t13) {
    t14 = <div className="flex min-w-0 max-w-full flex-col gap-5 overflow-hidden">{t11}{t12}{t13}</div>;
    $[58] = t11;
    $[59] = t12;
    $[60] = t13;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== dialogTitle || $[63] !== open || $[64] !== props.description || $[65] !== props.trigger || $[66] !== setOpen || $[67] !== t14) {
    t15 = <SilkSheet open={open} onOpenChange={setOpen} variant="detached" trigger={props.trigger} title={dialogTitle} description={props.description} size="default">{t14}</SilkSheet>;
    $[62] = dialogTitle;
    $[63] = open;
    $[64] = props.description;
    $[65] = props.trigger;
    $[66] = setOpen;
    $[67] = t14;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  return t15;
}